import { Component, inject, Injector, Input, OnInit } from '@angular/core';
import { find, matches } from '@sama/micro-dash';
import { Observable, Subject } from 'rxjs';
import { AbstractWorkspaceComponent } from '../../shared/abstract-workspace-component';
import { SamplingPortalResource } from '../../shared/resources/sampling-portal-resource.service';
import { DebounceSaveManagerService } from '../../shared/save-manager/debounce-save-manager.service';
import { SampleTask } from '../../team-apis/otterdam/sample-task';
import { QaFeedback, Task } from '../../team-apis/otterdam/task';
import { SampleLoadingService } from '../http/loading/sample-loading.service';
import { SampleTaskLoadingService } from '../http/loading/sample-task-loading.service';
import { isSavingWithMinimumDuration$ } from '../shared/navbar/saving-indicator/saving-indicator-helpers';
import { workspaceProviders } from '../workspace-providers';
import { createDefaultSamplingLayout } from './create-default-sampling-layout';
import {
  getContext,
  SamplingWorkspaceStore,
} from './state/sampling-workspace-store';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- some outputs don't load with on push
@Component({
  selector: 'h3-sampling-workspace',
  templateUrl: './sampling-workspace.component.html',
  styleUrls: ['./sampling-workspace.component.scss'],
  providers: workspaceProviders,
  standalone: false,
})
export class SamplingWorkspaceComponent
  extends AbstractWorkspaceComponent
  implements OnInit
{
  // make sure all inputs are also in SamplingWorkspaceHostComponent
  @Input({ required: true }) projectId!: number;
  @Input({ required: true }) sampleId!: number;
  @Input({ required: true }) initialPosition!: number;

  task!: Task;
  isSaving$: Observable<boolean>;

  private _isSaving$ = new Subject<boolean>();
  private sampleLoadingService = inject(SampleLoadingService);
  private sampleTaskLoadingService = inject(SampleTaskLoadingService);

  constructor(
    public samplingWorkspaceStore: SamplingWorkspaceStore,
    private samplingPortalResource: SamplingPortalResource,
    private debounceSaveManager: DebounceSaveManagerService<SampleTask>,
    injector: Injector,
  ) {
    super('sampling', injector);
    this.store.assign({
      readOnly: true,
      qaFeedbackIsEditable: true,
    });
    this.isSaving$ = isSavingWithMinimumDuration$(this._isSaving$, 2000);
  }

  override async ngOnInit() {
    this.debounceSaveManager.initialize(
      this,
      this.samplingWorkspaceStore('master'),
      this.samplingWorkspaceStore('workingCopy'),
      () => this.save(),
      ['comment'],
    );

    this.initializePosition();
    this.eventTrackingService.addContext({ sample_id: this.sampleId });
    this.store('feedbackType').set('shape');
    await super.ngOnInit();
  }

  protected buildWorkspaceConfig() {
    return { user: this.currentUser, ...this };
  }

  protected override async loadModels() {
    this.modelsLoading$.next('loading');

    await Promise.all([
      this.projectLoadingService.load(),
      this.featureFlagLoadingService.load(),
    ]);

    await Promise.all([
      this.sampleLoadingService.load(),
      this.loadTask(),
      this.sampleTaskLoadingService.load(),
      this.taxonomyService.load(),
    ]);
    this.settingsLoadingService.load();

    this.updateStepInfo();
    this.setTask(); // requires project to be loaded first

    await this.loadTaskModels();
  }

  protected updateStepInfo() {
    const { project } = this.store.state();
    const stepId = project.sampling_layout_step_id;
    if (stepId) {
      const step = project.steps.find(matches({ id: stepId }));
      if (!step) {
        throw new Error('That step does not exist on this project');
      }
      this.updateFromStepInfo(step);
    } else {
      this.updateFromStepInfo({
        layout: createDefaultSamplingLayout(project),
      });
    }
  }

  private async loadTask() {
    const { position } = this.samplingWorkspaceStore.state();
    this.task = await this.taskLoadingService.loadForSampling(position);
  }

  private setTask() {
    const master = sampleTaskOf(this.task, this.sampleId);
    this.samplingWorkspaceStore('master').set(master);
    this.debounceSaveManager.updateWorkingCopy();
    this.store.setTask(this.task);
  }

  private async loadTaskModels() {
    const task = this.store.state().task;
    await Promise.all([
      this.annotationLoadingService.load(task),
      this.feedbackLoadingService.load(),
      this.loadPreAnnotationsIfAllowed(),
    ]);
  }

  private async save() {
    const { master, workingCopy } = this.samplingWorkspaceStore.state();

    this._isSaving$.next(true);
    try {
      const sampleTaskAfter = await this.updateTask(master, workingCopy);
      this.messagingService.success('Changes saved');
      return sampleTaskAfter;
    } finally {
      this._isSaving$.next(false);
    }
  }

  private async updateTask(
    master: SampleTask,
    workingCopy: SampleTask,
  ): Promise<SampleTask> {
    const feedback: QaFeedback =
      await this.samplingPortalResource.updateScoreFromSampleTask({
        submissionId: this.store.getLastSubmission().id,
        stepId: this.store.getLastSubmission().step_id,
        projectId: this.projectId,
        taskId: this.task.id,
        round: master.round,
        context: getContext(master),
        sampleId: this.sampleId,
        sampleTask: { ...workingCopy, id: master.id },
      });

    return {
      ...master,
      // workingCopy is actually not a sample task... should probably be fixed
      // In the meantime we get master and override the values of
      // the working copy
      ...workingCopy,
      comment: feedback.comment,
    };
  }

  private initializePosition() {
    const searchParams = new URLSearchParams(
      this.browserControlService.getQueryParameters(),
    );
    const position = +(searchParams.get('position') ?? this.initialPosition);
    this.samplingWorkspaceStore('position').set(position);
  }
}

function sampleTaskOf(task: Task, sampleId: number): SampleTask {
  return {
    ...find(task.sample_tasks, matches({ sample_id: sampleId }))!,
  };
}
